import type {
  IMapLayoutCatalog,
  IMapLayoutElement,
  IMapLayoutElementGeometry,
  IMapLayoutElementSpots,
} from '@interfaces/map';

// UE owns the catalog of pre-built room elements and the arrangement — it auto-chains
// rooms by their exits. The web is only a picker: it chooses and orders element ids and
// never authors geometry, so every geometry field below is carried for display/debug
// only and is deliberately not sent back in SPAWN_LAYOUT.
//
// At runtime the catalog comes from the build: the MytaMapEditor GameCast metadata
// provider emits it under plugins.mapEditor, spatial-manager projects it onto the map
// record, and the hub reads map.layoutCatalog. A map whose build shipped none has no map
// editor at all — see resolveCatalogElements and useCanUseMapEditor.
// What a module is: the first of the three questions the picker asks. Kept a plain string
// rather than a union because a newer build may ship a family this hub has never heard of,
// and the picker groups on whatever it finds rather than dropping what it cannot name.
export const MODULE_FAMILY_ROOM = 'room';
export const MODULE_FAMILY_CORRIDOR = 'corridor';

// The walker's left/right — matching the art's _Left / _Right names and the sign of the
// module's yaw step. NOT the legacy `type`'s left/right, which were inverted.
export const MODULE_DIRECTION_STRAIGHT = 'straight';

// A cap, not a link. UE drops every room requested after one ('chain-closed'), so caps never
// appear in the picker and are appended to a published chain as its last room instead.
export type MapEditorElementRole = 'module' | 'end';

export interface MapEditorCatalogElement {
  id: string;
  displayName: string;
  category: string;
  // Authored prose, shown in the wall-layout step. Empty when the kit supplies none, which is the
  // normal case — the spot counts stand in for it.
  description: string;
  thumbnailUrl: string;
  // The pre-split fused type, carried for display and debugging only. Never sent back: UE reads
  // the family/direction pair now, and this field's left/right were backwards.
  type: string;
  family: string;
  direction: string;
  size: string;
  role: MapEditorElementRole;
  // Empty means the element is catalogued but has no room level built yet — UE spawns
  // nothing for it. Surfaced in the tree so an author is not left wondering why a room
  // they picked never appeared.
  geometryLevel: string;
  geometryClass: string;
  overrideExit: boolean;
  exitOffset: number[];
  exitYawDeg: number;
  // The build's own verdict on this element's geometry. Optional because the field post-dates the
  // rest of the catalog, so every reader below tolerates it missing rather than treating absence
  // as a negative answer.
  geometry?: IMapLayoutElementGeometry;
  // Absent means the build could not read the level, which is a different answer from a level
  // that really holds no spots. Both exist in the shipped kit.
  spots?: IMapLayoutElementSpots;
}

// 'fallback' means the map's build shipped no catalog. It carries no elements: useCanUseMapEditor
// gates every map-editor surface on 'build', so on a map without a kit there is no picker to fill,
// no inspector to look up ids in, and nothing to publish. A stand-in list existed here once and
// could not be reached from anywhere — worse, its ids were the test map's, so anything that did
// reach it would have offered rooms the map cannot build.
export type CatalogSource = 'build' | 'fallback';

const NO_CATALOG = {
  elements: [] as MapEditorCatalogElement[],
  source: 'fallback' as const,
  defaultEndElement: '',
};

// The pre-split `type` read as the pair, mapped by the turn each string actually produced
// rather than by the word in it: `corridor-left` swept toward +Y, which is the walker's
// *right*, and the catalog worked around that by labelling the art's _Right modules
// `corridor-left`. Mirrors MytaMapEditor::ParseLegacyType exactly, so a catalog shipped
// before the split builds the same world it always did.
const LEGACY_TYPE_FACETS: Record<
  string,
  { family: string; direction: string }
> = {
  'corridor-left': { family: MODULE_FAMILY_CORRIDOR, direction: 'right' },
  'corridor-right': { family: MODULE_FAMILY_CORRIDOR, direction: 'left' },
  gallery: {
    family: MODULE_FAMILY_ROOM,
    direction: MODULE_DIRECTION_STRAIGHT,
  },
};

// UE resolves an unnamed family to Room and an unnamed direction to Straight, so the same
// defaults are applied here: the picker's promise is that what it offers is what UE builds,
// and leaving these blank would put a real, spawnable element in a nameless bucket.
//
// Keyed on either field being *present*, not on it carrying a value, because that is what
// ParseCatalog does: an authored `"family": ""` is an answer — the default — and it stops UE
// looking at the legacy `type` entirely. Reading it as a gap instead sent the picker down the
// legacy mapping and offered a turn the element does not make.
const resolveFacets = (
  element: IMapLayoutElement,
): { family: string; direction: string } => {
  if (element.family !== undefined || element.direction !== undefined) {
    return {
      family: (element.family ?? '').trim() || MODULE_FAMILY_ROOM,
      direction: (element.direction ?? '').trim() || MODULE_DIRECTION_STRAIGHT,
    };
  }

  const legacy = LEGACY_TYPE_FACETS[(element.type ?? '').trim().toLowerCase()];

  return (
    legacy ?? {
      family: MODULE_FAMILY_ROOM,
      direction: MODULE_DIRECTION_STRAIGHT,
    }
  );
};

const normalizeElement = (
  element: IMapLayoutElement,
): MapEditorCatalogElement => ({
  id: element.id,
  displayName: element.displayName || element.id,
  // Ungrouped elements would otherwise vanish from a category-keyed tree.
  category: element.category || 'Other',
  description: element.description ?? '',
  thumbnailUrl: element.thumbnailUrl ?? '',
  type: element.type ?? '',
  ...resolveFacets(element),
  size: element.size ?? '',
  // Anything this hub cannot name is a module: 'end' is the role that changes behaviour, and
  // reading an unknown value as a cap would hide a pickable element from the picker.
  role: element.role === 'end' ? 'end' : 'module',
  geometryLevel: element.geometryLevel ?? '',
  geometryClass: element.geometryClass ?? '',
  overrideExit: element.overrideExit ?? false,
  exitOffset: element.exitOffset ?? [0, 0, 0],
  exitYawDeg: element.exitYawDeg ?? 0,
  // Passed through rather than flattened into booleans here: the shape is the build's, new
  // fields appear in it without a UE code change, and normalising early would drop them.
  geometry: element.geometry,
  spots: element.spots,
});

// Only an id this catalog can actually describe counts. UE emits the field only for a cap the
// build ships, but the catalog travels through spatial-manager and a DB record that may be
// older than the build, and an unresolvable id would publish a room with no geometry — a grey
// stub sealing a live world's layout.
//
// The geometry condition is the same one UE applies to its own auto-close (CloseLayoutChain:
// "the chain is left open rather than closed with a greybox stub"), and it has to be applied
// here too because the two halves append the cap on different paths: UE's runs only where a
// catalog can be read, and a packaged server reads none — so on the streamed path this hub is
// the only side that decides. Without it the web published exactly the grey box UE refuses to
// build, and the same map ended in a wall or in a placeholder depending on which side capped it.
//
// Deliberately the declared references rather than `hasBuiltGeometry`, which also fails an
// element whose level was left out of the cook. That case UE *does* build — it greyboxes it and
// the inspector marks the row — so rejecting it here would replace one divergence with another.
const resolveDefaultEndElement = (
  id: string | undefined,
  elements: MapEditorCatalogElement[],
): string => {
  const wanted = (id ?? '').trim();
  if (!wanted) {
    return '';
  }

  const element = elements.find((candidate) => candidate.id === wanted);
  if (element?.role !== 'end') {
    return '';
  }

  const declaresGeometry =
    !!element.geometryLevel.trim() || !!element.geometryClass.trim();

  return declaresGeometry ? element.id : '';
};

// Defensive on purpose: this payload crosses UE → metadata.json → spatial-manager →
// DB → API before arriving, so anything malformed falls back rather than throwing
// inside a render. An entry without an id is unusable — it is what SPAWN_LAYOUT sends.
export const resolveCatalogElements = (
  catalog: IMapLayoutCatalog | undefined | null,
): {
  elements: MapEditorCatalogElement[];
  source: CatalogSource;
  defaultEndElement: string;
} => {
  const rawElements = catalog?.elements;
  if (!Array.isArray(rawElements)) {
    return NO_CATALOG;
  }

  const elements = rawElements
    .filter(
      (element): element is IMapLayoutElement =>
        !!element && typeof element.id === 'string' && element.id.length > 0,
    )
    .map(normalizeElement);

  // An empty array from the build is treated as "no catalog shipped" rather than
  // "a catalog of nothing" — the latter would leave the author with a dead panel
  // and no signal about why.
  if (!elements.length) {
    return NO_CATALOG;
  }

  return {
    elements,
    source: 'build',
    // Resolved here rather than passed through, because a cap is only useful if it can be
    // described on the wire: a packaged server reads no catalog, so an id naming an element
    // this catalog does not carry would publish a room with no geometry to resolve it from.
    defaultEndElement: resolveDefaultEndElement(
      catalog?.defaultEndElement,
      elements,
    ),
  };
};

export const findCatalogElement = (
  elements: MapEditorCatalogElement[],
  id: string,
): MapEditorCatalogElement | undefined =>
  elements.find((element) => element.id === id);

/**
 * Whether a chain already ends in a cap, and so must not be given another.
 *
 * The picker never offers one, but a chain restored from the branding record can still end in a cap:
 * the flat catalog list that preceded the picker had no notion of a role and listed every element,
 * so a layout saved then may carry one as a room the author picked.
 *
 * Shared by the two places that have to agree about it — the payload builder, which would otherwise
 * append a second cap UE refuses as `chain-closed`, and the Layout panel, which would otherwise show
 * two walls for the one the payload carries.
 *
 * @param roomIds - The chain, in order.
 * @param elements - The resolved catalog. An id it cannot describe is not a cap.
 * @returns True when the last room is an End element.
 */
export const chainEndsInCap = (
  roomIds: string[],
  elements: MapEditorCatalogElement[],
): boolean => {
  const lastId = roomIds[roomIds.length - 1];

  return !!lastId && findCatalogElement(elements, lastId)?.role === 'end';
};

/**
 * The elements an author may put in a chain: everything except the caps.
 *
 * An End element seals the chain — UE drops every room requested after one and reports them
 * as `chain-closed` — so offering one in the picker is offering a way to silently delete the
 * rest of the layout. The cap is appended to a published chain instead, as its last room.
 *
 * @param elements - The resolved catalog.
 * @returns The pickable modules, in catalog order.
 */
export const getPickableElements = (
  elements: MapEditorCatalogElement[],
): MapEditorCatalogElement[] =>
  elements.filter((element) => element.role !== 'end');

// Whether this build can actually put art on the ground for this element.
//
// Prefers the build's own verdict over the declared path, because the two disagree in the case
// that matters: an element can name a room level the build never cooked, and the declaration
// looks fine while the spawn comes out a placeholder box.
export const hasBuiltGeometry = (element: MapEditorCatalogElement): boolean => {
  const { geometry } = element;
  if (!geometry) {
    // A build that predates the verdict field. The declared level is the only evidence there is —
    // which is what this check has always used.
    return (element.geometryLevel ?? '').trim().length > 0;
  }

  // `cooked` is conditionally absent by design (class geometry, or an unreadable cook list) and
  // absent means unknown, so only an explicit false counts against it.
  return (
    geometry.kind !== 'greybox' &&
    geometry.available &&
    geometry.cooked !== false
  );
};

// The element declares art this build cannot supply — a level that does not resolve, or one left
// out of the cook. UE greyboxes it, so the author gets a placeholder box where they expect a
// room, and nothing else in the UI would say why.
//
// Deliberately distinct from "no art yet" (`kind: 'greybox'`), which is an intentional state and
// not a problem to report. Only a build can tell these apart, so this returns false whenever the
// verdict is missing rather than guessing from the declared path.
export const isGeometryMisconfigured = (
  element: MapEditorCatalogElement,
): boolean => {
  const { geometry } = element;
  if (!geometry || geometry.kind === 'greybox') {
    return false;
  }

  return !geometry.available || geometry.cooked === false;
};
